import './IntegrationSettingsPopup.css';
import React, { useState, useRef, useEffect } from 'react';
import Button from '../../Button/Button';
import { IntegrationFormZendesk } from 'src/components/Integrations/IntegrationForms/IntegrationFormZendesk/IntegrationFormZendesk';
import { IntegrationFormSlack } from 'src/components/Integrations/IntegrationForms/IntegrationFormSlack/IntegrationFormSlack';
import { useNotification } from 'src/hooks/useNotification';
import { useParams } from 'react-router-dom';
import { useLoader } from 'src/provider/LoaderProvider';
import { SlackIntegrationLink } from 'src/components/Integrations/IntegrationForms/IntegrationFormSlack/SlackIntegrationLink';
import { IntegrationFormFreshdesk } from 'src/components/Integrations/IntegrationForms/IntegrationFormFreshdesk/IntegrationFormFreshdesk';
import { useUpdateIntegrationMutation } from 'src/services';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import { EditIcon } from 'src/assets/icons/editIcon';
import { IntegrationFormZohoSalesIqChat } from 'src/components/Integrations/IntegrationForms/IntegrationFormZohoSalesIqChat/IntegrationFormZohoSalesIqChat';
import { IntegrationFormZendeskChat } from 'src/components/Integrations/IntegrationForms/IntegrationFormZendeskChat/IntegrationFormZendeskChat';
import { IntegrationFormZendeskEmail } from 'src/components/Integrations/IntegrationForms/IntegrationFormZendeskEmail/IntegrationFormZendeskEmail';
import { IntegrationFormFreshdeskEmail } from 'src/components/Integrations/IntegrationForms/IntegrationFormFreshdeskEmail/IntegrationFormFreshdeskEmail';
import { IntegrationFormFreshdeskChat } from 'src/components/Integrations/IntegrationForms/IntegrationFormFreshdeskChat/IntegrationFormFreshdeskChat';
import { IntegrationFormZohoEmail } from 'src/components/Integrations/IntegrationForms/IntegrationFormZohoEmail/IntegrationFormZohoEmail';
import { IntegrationFormTrengo } from 'src/components/Integrations/IntegrationForms/IntegrationFormTrengo/IntegrationFormTrengo';

export const IntegrationSettingsPopup = ({
  integration,
  disabled = false,
  isOpen: _isOpen = false,
  trigger = (
    <div className="edit-button">
      <EditIcon />
    </div>
  ),
}) => {
  const [open, setOpen] = useState(false);
  const notification = useNotification();
  const { id } = useParams();
  const [integrationType, setIntegrationType] = useState(integration.integration_type);
  const loader = useLoader();
  const popupContentRef = useRef();
  const [
    updateIntegration,
    { isSuccess: isSuccessUpdateIntegration, isLoading: isLoadingUpdateIntegration },
  ] = useUpdateIntegrationMutation();

  const submit = async (formData) => {
    updateIntegration({
      projectId: id,
      integrationId: formData.id,
      body: formData,
    });
  };

  useEffect(() => {
    if (isSuccessUpdateIntegration) {
      notification.success('Integration Updated.');
      setOpen(false);
    }
  }, [isSuccessUpdateIntegration]);

  useEffect(() => {
    if (isLoadingUpdateIntegration) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingUpdateIntegration]);

  const renderForm = (popupContentRef) => {
    switch (integrationType) {
      case 'zendesk':
        return (
          <IntegrationFormZendesk
            integration={integration}
            onSubmit={(formData) => submit(formData)}
          />
        );
      case 'zendesk_chat':
        return (
          <IntegrationFormZendeskChat
            integration={integration}
            onSubmit={(formData) => submit(formData)}
          />
        );
      case 'zoho_salesiq':
        return (
          <IntegrationFormZohoSalesIqChat
            integration={integration}
            onSubmit={(formData) => submit(formData)}
          />
        );
      case 'zoho_desk':
        return (
          <IntegrationFormZohoEmail
            integration={integration}
            onSubmit={(formData) => submit(formData)}
          />
        );
      case 'zendesk_email':
        return (
          <IntegrationFormZendeskEmail
            integration={integration}
            onSubmit={(formData) => submit(formData)}
          />
        );
      case 'freshdesk_email':
        return (
          <IntegrationFormFreshdeskEmail
            integration={integration}
            onSubmit={(formData) => submit(formData)}
          />
        );
      case 'freshdesk_chat':
        return (
          <IntegrationFormFreshdeskChat
            integration={integration}
            onSubmit={(formData) => submit(formData)}
          />
        );
      case 'slack':
        return (
          <IntegrationFormSlack
            popupContentRef={popupContentRef}
            integration={integration}
            onSubmit={(formData) => submit(formData)}
          />
        );
      case 'freshdesk':
        return (
          <IntegrationFormFreshdesk
            popupContentRef={popupContentRef}
            integration={integration}
            onSubmit={(formData) => submit(formData)}
          />
        );
      case 'trengo':
        return (
          <IntegrationFormTrengo
            integration={integration}
            onSubmit={(formData) => submit(formData)}
          />
        );
      default:
        return null;
    }
  };

  const renderHelpContainer = () => {
    if (integrationType === 'slack') {
      return <SlackIntegrationLink link={integration.integration_data.slack_url} />;
    }
    return null;
  };

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger
        disabled={disabled}
        asChild
        className={clsx(disabled && 'pointer-events-none opacity-50', 'cursor-pointer')}
      >
        {trigger}
      </DialogTrigger>
      <DialogContent className={`integration-popup ${integrationType}`}>
        <DialogHeader>
          <DialogTitle>Integration Settings</DialogTitle>
        </DialogHeader>
        <div className="content" ref={popupContentRef}>
          {integrationType && renderForm(popupContentRef)}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button style={{ width: 'auto' }} className="transparent black">
              Cancel
            </Button>
          </DialogClose>
          <Button style={{ width: 'auto' }} type="submit" form="integration-form" className="blue">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
